import type { ReactNode } from 'react';
import { Icon } from '../atoms/Icon';
import type { IconName } from '../icons/registry';
import styles from './Timeline.module.css';

export type TimelineTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export interface TimelineItem {
  key: string;
  icon: IconName;
  tone?: TimelineTone;
  /** Formatted date (or time) of the entry. */
  date: ReactNode;
  /** `dateTime` attribute (ISO) for the date. */
  dateTime?: string;
  title: ReactNode;
  description?: ReactNode;
  /** Small actions on the right, e.g. edit. */
  actions?: ReactNode;
  /** Entries with the same group label are shown under one heading (e.g. a month). */
  group?: string;
}

export interface TimelineProps {
  items: TimelineItem[];
  /** Accessible name of the list. */
  label: string;
}

/** Chronological list of events, grouped e.g. by month: small symbol, text, date. */
export function Timeline({ items, label }: TimelineProps) {
  const groups: { label: string | undefined; items: TimelineItem[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.label === item.group) last.items.push(item);
    else groups.push({ label: item.group, items: [item] });
  }
  return (
    <div className={styles.timeline} role="group" aria-label={label}>
      {groups.map((g, i) => (
        <section key={`${g.label ?? ''}-${i}`} className={styles.group}>
          {g.label && <h3 className={styles.groupLabel}>{g.label}</h3>}
          <ol className={styles.list}>
            {g.items.map((item) => (
              <li key={item.key} className={styles.item}>
                <Icon
                  name={item.icon}
                  size="sm"
                  className={[styles.icon, styles[item.tone ?? 'neutral']].join(' ')}
                />
                <div className={styles.body}>
                  <div className={styles.head}>
                    <span className={styles.title}>{item.title}</span>
                    <time className={styles.date} dateTime={item.dateTime}>
                      {item.date}
                    </time>
                  </div>
                  {item.description && <div className={styles.description}>{item.description}</div>}
                </div>
                {item.actions && <div className={styles.actions}>{item.actions}</div>}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
